import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ErrorMessage, InfoMessage } from "../../../_UI/Components/Alerts/PathwayAlertMessages";
import { Modal, ModalBody, ModalHeader } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { prepareDatevUOOnboarding, removeDatevConnection } from "../services/clients.actions";
import LoadingSpinner from "../../../_UI/Components/LoadingSpinner";
import listUtils from "../../../_services/list-utils";
import { LoadingSpinnerButton } from "../../../_UI/Components/PathwayButtons";
import stringUtils from "../../../_services/string-utils";
import dateUtils from "../../../_services/date-utils";
import { AuthDataStatus } from "../../../_statics/AuthDataStatus";
import { TableContentDividerLine } from "../../../_UI/StyledComponents/PathwayTable";
import { CircleIcon } from "../../../_UI/StyledComponents/PathwayIcons";
import { getOrganization } from "../../myOrganization/services/store/myOrganization.actions";
import ConfirmationModal from "../../../features/PaymentsMatching/components/ConfirmationModal";
import PathwayStatusPill from "../../../_UI/Components/PathwayStatusPill";

const FeeString = () => {
    return (
        <p>
            Bitte beachte, dass du mit erfolgreicher Verbindung des DATEV Buchungsdatenservice einer monatlichen Gebühr
            von 25€ zustimmst.
            <br />
            "Bronze" Steuerberaterpartner sind von den Zusatzkosten ausgenommen.{" "}
            <a
                href="https://hilfe.pathway-solutions.de/de/knowledge/steuerberatung/pathway-siegelpartnerprogramm-kanzleien"
                target="_blank"
                rel="noopener noreferrer"
            >
                Zum Hilfeartikel
            </a>
        </p>
    );
};

export const DatevConnection = ({ showModal, closeModal, availableConnections }) => {
    const myOrganization = useSelector((state) => state.myOrganization);
    const clientReducer = useSelector((state) => state.client);

    const dispatch = useDispatch();

    const [datevClientToBeConfirmed, setDatevClientToBeConfirmed] = useState(false);

    useEffect(() => {
        dispatch(getOrganization());
    }, [dispatch]);

    const isGoldCustomer = useMemo(() => {
        return (
            myOrganization.organization &&
            myOrganization.organization.billingAgreementType &&
            stringUtils.contains(myOrganization.organization.billingAgreementType, "GOLD")
        );
    }, [myOrganization.organization]);

    const existingConnections = useMemo(() => {
        if(Array.isArray(clientReducer.existingDatevConnections)){
            return [
            ...new Map(
                 [...clientReducer.existingDatevConnections]
                    .sort((a, b) => {
                        const splits1 = a.identifier.split("-");
                        const splits2 = b.identifier.split("-");
                        return Number(splits1[0]) > Number(splits2[0])
                            ? 1
                            : Number(splits1[0]) < Number(splits2[0])
                              ? -1
                              : Number(splits1[1]) > Number(splits2[1])
                                ? 1
                                : Number(splits1[1]) < Number(splits2[1])
                                  ? -1
                                  : 0;
                    })
                    .map((authData) => {
                        const splits = authData.identifier.split("-");
                        return [
                            splits[0] + "/" + splits[1],
                            {
                                ...authData,
                                consultantNumber: splits[0],
                                clientNumber: splits[1],
                            },
                        ];
                    })
            ).values(),
        ];
        } else {
            return [];
        }
        
    }, [clientReducer.existingDatevConnections]);

    const selectableConnections = useMemo(() => {
        return availableConnections.filter((c1) => {
            return (
                existingConnections.findIndex(
                    (c2) => c1.consultantNumber === c2.consultantNumber && c1.clientNumber === c2.clientNumber
                ) < 0
            );
        });
    }, [availableConnections, existingConnections]);

    const initiateDatevAuthorization = useCallback(
        (consultantNumber, clientNumber) => {
            if (isGoldCustomer || !listUtils.isEmpty(existingConnections) || !!datevClientToBeConfirmed) {
                dispatch(
                    prepareDatevUOOnboarding({
                        consultantNumber: consultantNumber,
                        clientNumber: clientNumber,
                    })
                ).then((response) => {
                    if (response.authLink) {
                        window.location.href = response.authLink;
                        setDatevClientToBeConfirmed(null);
                    }
                });
            } else {
                setDatevClientToBeConfirmed({ consultantNumber: consultantNumber, clientNumber: clientNumber });
            }
        },
        [dispatch, isGoldCustomer, existingConnections, datevClientToBeConfirmed, setDatevClientToBeConfirmed]
    );

    const removeConnection = useCallback(
        (consultantNumber, clientNumber) => {
            dispatch(
                removeDatevConnection({
                    consultantNumber: consultantNumber,
                    clientNumber: clientNumber,
                })
            );
        },
        [dispatch]
    );

    return (
        <>
            <ConfirmationModal
                isOpen={
                    !!datevClientToBeConfirmed ||
                    (!isGoldCustomer &&
                        listUtils.isEmpty(existingConnections) &&
                        !!clientReducer.addDatevConnectionLoadingId)
                }
                toggleConfirmationModal={() => setDatevClientToBeConfirmed(null)}
                title="DATEV Buchungsdatenservice Verbindung"
                loading={!!clientReducer.addDatevConnectionLoadingId}
                buttonSubmit={() =>
                    initiateDatevAuthorization(
                        datevClientToBeConfirmed.consultantNumber,
                        datevClientToBeConfirmed.clientNumber
                    )
                }
            >
                <FeeString />
                <br />
                <p>Die Zusatzgebühr wird mit der monatlichen Rechnung abgerechnet.</p>
            </ConfirmationModal>

            <Modal isOpen={showModal} size="xl">
                <ModalHeader toggle={closeModal}>DATEV Buchungsdatenservice Verbindungen</ModalHeader>
                <ModalBody>
                    <InfoMessage useBottomMargin>
                        Mit einem Klick auf den Button wirst du zu Datev weitergeleitet um uns die Berechtigung zu der
                        gewählten Kombination aus Berater- und Mandantennummer zu erteilen. Alle Accounts mit der
                        gleichen Datev-Einstellung werden automatisch mit verbunden.
                        <br />
                        <br />
                        <b>
                            <FeeString />
                        </b>
                        <br />
                        <br />
                        Der DATEV Buchungsdatenservice muss vor der Nutzung bei DATEV bestellt und in Betrieb genommen
                        werden (
                        <a
                            href="https://www.datev.de/web/de/mydatev/datev-datenservices/buchungsdatenservice-einrichten/"
                            target="_blank"
                            rel="noreferrer"
                        >
                            Link zur DATEV Hilfe
                        </a>
                        ).
                        <br />
                        <br />
                        In unserem Hilfecenter findest du eine Übersicht über alle wichtigen Informationen zur
                        Einrichtung:
                        <br />
                        <a
                            href="https://hilfe.pathway-solutions.de/de/knowledge/datev#buchungsdatenservice"
                            target="_blank"
                            rel="noreferrer"
                        >
                            Übersicht: Einrichtung und Nutzung des DATEV Buchungsdatenservice bei pathway
                        </a>
                        <br />
                        <br />
                    </InfoMessage>

                    <ErrorMessage useTopMargin useBottomMargin={false}>
                        {clientReducer.getDatevConnectionsErrorMessage}
                    </ErrorMessage>

                    {!(clientReducer.getDatevConnectionsLoading || clientReducer.getDatevConnectionsErrorMessage) && (
                        <>
                            {!listUtils.isEmpty(existingConnections) && (
                                <ConnectionLayout data-cy="existing-connections-wrapper">
                                    <br />
                                    <h5>Bestehende Verbindungen</h5>
                                    <TableContentDividerLine />
                                    {existingConnections.map((authData, index) => {
                                        return (
                                            <>
                                                <ConnectionRow key={index}>
                                                    <VerticalCell>
                                                        <div>
                                                            <p>Berater-Nr.: {authData.consultantNumber}</p>
                                                            <p>Mandanten-Nr.: {authData.clientNumber}</p>
                                                        </div>
                                                        {authData.note && <p>Authorisiert von {authData.note}</p>}
                                                        <DatevConnectionBadge authData={authData} />
                                                    </VerticalCell>

                                                    <ConnectionRowCell>
                                                        <TrafficLightLayout>
                                                            <CircleIcon
                                                                style={{
                                                                    color:
                                                                        authData.status === AuthDataStatus.VALID
                                                                            ? "green"
                                                                            : authData.status === AuthDataStatus.INVALID
                                                                              ? "red"
                                                                              : "orange",
                                                                    fontSize: "1.8rem",
                                                                    background: "black",
                                                                    borderRadius: "90%",
                                                                }}
                                                            />
                                                        </TrafficLightLayout>

                                                        <ButtonLayout>
                                                            <LoadingSpinnerButton
                                                                color="secondary"
                                                                onClick={() =>
                                                                    initiateDatevAuthorization(
                                                                        authData.consultantNumber,
                                                                        authData.clientNumber
                                                                    )
                                                                }
                                                                loading={
                                                                    clientReducer.addDatevConnectionLoadingId ===
                                                                    authData.consultantNumber +
                                                                        "-" +
                                                                        authData.clientNumber
                                                                }
                                                                disabled={clientReducer.addDatevConnectionLoadingId}
                                                                buttonCyData={
                                                                    "renew-datev-connection-" + index + "-button"
                                                                }
                                                            >
                                                                Erneuern
                                                            </LoadingSpinnerButton>
                                                            <LoadingSpinnerButton
                                                                color="danger"
                                                                onClick={() =>
                                                                    removeConnection(
                                                                        authData.consultantNumber,
                                                                        authData.clientNumber
                                                                    )
                                                                }
                                                                loading={
                                                                    !!(
                                                                        clientReducer.removeDatevConnectionLoadingId &&
                                                                        clientReducer.removeDatevConnectionLoadingId ===
                                                                            authData.consultantNumber +
                                                                                "-" +
                                                                                authData.clientNumber
                                                                    )
                                                                }
                                                                disabled={
                                                                    !stringUtils.isEmpty(
                                                                        clientReducer.removeDatevConnectionLoadingId
                                                                    )
                                                                }
                                                                buttonCyData={
                                                                    "remove-datev-connection-" + index + "-button"
                                                                }
                                                            >
                                                                Entfernen
                                                            </LoadingSpinnerButton>
                                                        </ButtonLayout>
                                                    </ConnectionRowCell>
                                                </ConnectionRow>
                                            </>
                                        );
                                    })}
                                </ConnectionLayout>
                            )}

                            {!listUtils.isEmpty(selectableConnections) && (
                                <ConnectionLayout data-cy="available-connections-wrapper">
                                    <br />
                                    <h5>Verfügbare Verbindungen</h5>
                                    <TableContentDividerLine />
                                    {selectableConnections.map((availableConnection, index) => {
                                        return (
                                            <>
                                                <ConnectionRow key={index}>
                                                    <VerticalCell>
                                                        <p>Berater-Nr.: {availableConnection.consultantNumber}</p>
                                                        <p>Mandanten-Nr.: {availableConnection.clientNumber}</p>
                                                    </VerticalCell>

                                                    <TrafficLightLayout>
                                                        <CircleIcon
                                                            style={{
                                                                color: "var(--pw-danger)",
                                                                fontSize: "1.8rem",
                                                                background: "black",
                                                                borderRadius: "90%",
                                                            }}
                                                        />
                                                    </TrafficLightLayout>

                                                    <LoadingSpinnerButton
                                                        onClick={() =>
                                                            initiateDatevAuthorization(
                                                                availableConnection.consultantNumber,
                                                                availableConnection.clientNumber
                                                            )
                                                        }
                                                        loading={
                                                            clientReducer.addDatevConnectionLoadingId ===
                                                            availableConnection.consultantNumber +
                                                                "-" +
                                                                availableConnection.clientNumber
                                                        }
                                                        buttonCyData={"add-datev-connection-" + index + "-button"}
                                                    >
                                                        Verbindung herstellen
                                                    </LoadingSpinnerButton>
                                                </ConnectionRow>
                                                <TableContentDividerLine />
                                            </>
                                        );
                                    })}
                                </ConnectionLayout>
                            )}
                        </>
                    )}

                    <LoadingSpinner size="4em" loading={clientReducer.getDatevConnectionsLoading} />

                    {listUtils.isEmpty(availableConnections) && (
                        <ErrorMessage useTopMargin={true} useBottomMargin={false}>
                            Es existiert noch kein Account mit hinterlegter Berater- und Mandantennummer. Zum Verbinden
                            mit Datev muss mindestens ein solcher Account existieren.
                        </ErrorMessage>
                    )}

                    <ErrorMessage useTopMargin useBottomMargin={false}>
                        {stringUtils.combineStrings(
                            clientReducer.addDatevConnectionErrorMessage,
                            clientReducer.removeDatevConnectionErrorMessage,
                            "\n"
                        )}
                    </ErrorMessage>
                </ModalBody>
            </Modal>
        </>
    );
};

const DatevConnectionBadge = ({ authData }) => {
    return (
        <BadgeLayout>
            {authData.status === AuthDataStatus.VALID && (
                <PathwayStatusPill severity="ok">
                    Gültig{" "}
                    {authData.expiringAt &&
                        "bis: " +
                            dateUtils.formatToGermanDateString(new Date(authData.expiringAt)) +
                            " " +
                            dateUtils.formatToGermanTimeString(new Date(authData.expiringAt))}
                </PathwayStatusPill>
            )}

            {authData.status === AuthDataStatus.EXPIRED && <PathwayStatusPill severity="warning">Abgelaufen</PathwayStatusPill>}

            {authData.status === AuthDataStatus.INVALID && (
                <div>
                    <PathwayStatusPill severity="error">Verbindung verloren</PathwayStatusPill>
                </div>
            )}

            {authData.status === AuthDataStatus.SETUP && (
                <div>
                    <PathwayStatusPill severity="warning">Nicht vollständig eingerichtet</PathwayStatusPill>
                    <a
                        href="https://hilfe.pathway-solutions.de/de/knowledge/rechtevergabe-datev-buchungsdatenservice"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Zum Hilfeartikel
                    </a>
                </div>
            )}
        </BadgeLayout>
    );
};

const ConnectionLayout = styled.div`
    display: grid;
    margin-bottom: 1em;

    & h5 {
        margin-bottom: 0;
    }

    row-gap: var(--gap-l);
    margin-top: var(--gap-xxl);
`;

const ConnectionRow = styled.div`
    display: grid;
    align-items: center;
    justify-content: space-between;
    column-gap: var(--gap-l);
    row-gap: var(--gap-l);
    grid-template-columns: repeat(auto-fit, minmax(250px, max-content));
`;

const ConnectionRowCell = styled.div`
    display: grid;
    align-items: center;
    // justify-content: space-between;
    column-gap: var(--gap-l);
    grid-template-columns: min-content max-content;
`;

const TrafficLightLayout = styled.div`
    text-align: right;
`;

const BadgeLayout = styled.div`
    display: grid;
    grid-template-columns: min-content;
`;

const ButtonLayout = styled.div`
    display: grid;
    grid-template-columns: auto auto;
    grid-template-rows: max-content;
    row-gap: var(--gap-l);
    column-gap: var(--gap-l);
    align-items: end;
    align-content: space-around;
    height: 100%;
`;

const VerticalCell = styled.div`
    display: grid;
    grid-template-rows: min-content min-content;
    align-content: space-between;
    height: 100%;

    & p {
        margin: 0;
    }
`;
